import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface CandidateSite { id: number; siteName: string | null; siteCode: string | null; projectId: number | null; clientId: number | null }
export interface MonitoringAttachment { id: number; fileUrl: string; fileName: string | null }
export interface MonitoringItem {
  id: number; serviceType: string; subServiceType: string;
  status: 'pending' | 'in_progress' | 'done' | 'na'; percentComplete: number; remarks: string | null; sortOrder: number;
  attachments?: MonitoringAttachment[];
  completionDate?: string | null; tatDays?: number | null;
}
export interface ServiceRollup { serviceType: string; done: number; total: number; percent: number }
export interface SiteMonitoring {
  id: number; code: string; siteId: number; projectId: number | null; clientId: number | null;
  title: string | null; notes: string | null; status: 'active' | 'completed' | 'on_hold'; createdAt: string;
  siteName?: string | null; siteCode?: string | null; siteAddress?: string | null; siteStatus?: string | null;
  clientName?: string | null; clientCode?: string | null; clientContact?: string | null; clientMobile?: string | null; clientEmail?: string | null;
  projectName?: string | null; projectCode?: string | null;
  allocationDate?: string | null; clientCommittedDate?: string | null; plannedTatDays?: number | null;
  stateName?: string | null; projectManagerName?: string | null; romName?: string | null;
  vendorName?: string | null; vendorMobile?: string | null; vendorEmail?: string | null;
  technicianName?: string | null; technicianMobile?: string | null;
  overallPercent: number;
  currentStage?: string | null; currentStagePercent?: number | null;
  items?: MonitoringItem[]; services?: ServiceRollup[];
}

export interface ProjectRollupSite {
  monitoringId: number; code: string; siteId: number; siteName: string | null; siteCode: string | null;
  status: string; overallPercent: number; currentStage: string | null;
}
export interface ProjectRollup {
  projectId: number; siteCount: number; cumulativePercent: number;
  services: { serviceType: string; percent: number }[];
  sites: ProjectRollupSite[];
}

export interface ClientRollupProject {
  projectId: number | null; projectName: string | null; projectCode: string | null;
  siteCount: number; cumulativePercent: number;
  services: { serviceType: string; percent: number }[];
  sites: ProjectRollupSite[];
}
export interface ClientRollup {
  clientId: number; projectCount: number; siteCount: number; projects: ClientRollupProject[];
}

export const epcSiteMonitoringApi = {
  projectRollup: async (projectId: number): Promise<ProjectRollup> =>
    (await apiClient.get<ApiSuccess<ProjectRollup>>(`/epc-site-monitoring/project/${projectId}/rollup`)).data.data,
  clientRollup: async (clientId: number): Promise<ClientRollup> =>
    (await apiClient.get<ApiSuccess<ClientRollup>>(`/epc-site-monitoring/client/${clientId}/rollup`)).data.data,
  async list(params: Record<string, unknown> = {}): Promise<{ data: SiteMonitoring[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<SiteMonitoring[]>>('/epc-site-monitoring', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  // Sites pickable for a new monitoring record — EPC-scoped, not region-gated, excludes already-monitored sites.
  candidateSites: async (): Promise<CandidateSite[]> =>
    (await apiClient.get<ApiSuccess<CandidateSite[]>>('/epc-site-monitoring/candidate-sites')).data.data,
  get: async (id: number) => (await apiClient.get<ApiSuccess<SiteMonitoring>>(`/epc-site-monitoring/${id}`)).data.data,
  create: async (body: { siteId: number; title?: string; notes?: string }) => (await apiClient.post<ApiSuccess<SiteMonitoring>>('/epc-site-monitoring', body)).data.data,
  updateItem: async (itemId: number, body: { status?: string; percentComplete?: number; remarks?: string; completionDate?: string | null }) =>
    (await apiClient.put<ApiSuccess<SiteMonitoring>>(`/epc-site-monitoring/items/${itemId}`, body)).data.data,
  addAttachment: async (itemId: number, body: { fileUrl: string; fileName?: string }) =>
    (await apiClient.post<ApiSuccess<SiteMonitoring>>(`/epc-site-monitoring/items/${itemId}/attachments`, body)).data.data,
  removeAttachment: async (attId: number) =>
    (await apiClient.delete<ApiSuccess<SiteMonitoring>>(`/epc-site-monitoring/attachments/${attId}`)).data.data,
};
